import { useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatAUD, isNegative } from "@/lib/format";
import { downloadBlob } from "@/lib/csv";
import FyTabs from "@/components/FyTabs";

const Block = ({ title, rows, total, totalLabel, prefix }) => (
  <div className="border border-border bg-card p-6" data-testid={`pnl-${prefix}`}>
    <h3 className="font-display text-xl font-semibold">{title}</h3>
    <div className="mt-4">
      {rows.map((r) => (
        <div key={r.name} className="flex justify-between py-2 text-sm border-b border-border/60 last:border-0">
          <span className={r.name === "Uncategorised" ? "text-amber-500" : "text-muted-foreground"}>{r.name}</span>
          <span className="font-mono">{formatAUD(r.amount)}</span>
        </div>
      ))}
      <div className="flex justify-between pt-3 mt-1 border-t border-border font-semibold">
        <span>{totalLabel}</span><span className="font-mono" data-testid={`pnl-${prefix}-total`}>{formatAUD(total)}</span>
      </div>
    </div>
  </div>
);

export default function ProfitLossPage() {
  const api = useApi();
  const { data: fys } = useResource("/financial_years");
  const [fy, setFy] = useState(null);
  const { data: p } = useResource(fy ? `/reports/pnl?fy=${fy}` : null);
  const pdf = async () => {
    try { downloadBlob(`Profit-and-loss-FY-${fy}.pdf`, await api("get", `/reports/pnl/pdf?fy=${fy}`, undefined, { responseType: "blob" })); }
    catch (e) { toast.error(errorText(e)); }
  };
  const loss = p && isNegative(p.net);
  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold" data-testid="pnl-title">Profit &amp; loss</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Income less expenses for one financial year, and each director's share.</p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <FyTabs fys={fys} value={fy} onChange={setFy} prefix="pnl" />
        <Button className="ml-auto rounded-full" disabled={!p} onClick={pdf} data-testid="pnl-pdf-button"><FileDown className="h-4 w-4 mr-2" /> Download PDF</Button>
      </div>
      {p && (
        <div className="mt-6 space-y-5">
          <div className="border border-border bg-card p-6 flex flex-wrap items-end gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{loss ? "Net loss" : "Net profit"} · FY {p.fy}</div>
              <div className={`font-mono text-4xl mt-2 ${loss ? "text-rose-500" : "text-emerald-500"}`} data-testid="pnl-net">{formatAUD(p.net)}</div>
            </div>
            {p.margin && <div className="text-sm text-muted-foreground" data-testid="pnl-margin">{Number(p.margin).toFixed(1)}% of income</div>}
            <div className="text-sm text-muted-foreground ml-auto">{p.rows} ledger rows</div>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            <Block title="Income" rows={p.income} total={p.income_total} totalLabel="Total income" prefix="income" />
            <Block title="Expenses" rows={p.expenses} total={p.expense_total} totalLabel="Total expenses" prefix="expenses" />
          </div>
          <div className="grid gap-4 md:grid-cols-3" data-testid="pnl-shares">
            {p.shares.map((s) => (
              <div key={s.name} className="border border-border bg-card p-5" data-testid={`pnl-share-${s.name.toLowerCase()}`}>
                <div className="flex justify-between text-sm"><span className="font-display text-lg font-semibold">{s.name}</span><span className="font-mono text-muted-foreground">{s.share_pct}%</span></div>
                <div className="font-mono text-2xl mt-2">{formatAUD(s.amount)}</div>
                <div className="text-xs text-muted-foreground mt-1">share of the {loss ? "loss" : "profit"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
